import { PublicClientApplication, EventType } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { attachMsal } from './api/client';
import { msalConfig } from './auth/config';
import { noteRedirectFailure } from './auth/session';
import { ThemeProvider } from './theme/ThemeProvider';
import { TimezoneProvider } from './time/TimezoneProvider';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry policy is set per hook, because a 403 must not be retried and the default
      // would turn one clear denial into three requests. See api/hooks.ts.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
});

const msal = new PublicClientApplication(msalConfig);

/**
 * MSAL v3 requires an explicit `initialize()` before any other call, and
 * `handleRedirectPromise()` must complete before the app reads accounts — otherwise the first
 * render after returning from the Entra redirect sees no account and shows the sign-in screen
 * to a user who has just signed in.
 */
async function bootstrap(): Promise<void> {
  await msal.initialize();

  // A rejection here is Entra declining the sign-in — consent withheld, conditional access,
  // an unregistered reply URL — not a broken build, and the gate can render it with a retry.
  // Letting it reach the fatal handler below would replace the whole app with an error page,
  // which since sign-in became automatic is a state the user can arrive at without clicking
  // anything and cannot leave.
  try {
    const result = await msal.handleRedirectPromise();
    if (result?.account) {
      msal.setActiveAccount(result.account);
    } else if (!msal.getActiveAccount()) {
      const [first] = msal.getAllAccounts();
      if (first) msal.setActiveAccount(first);
    }
  } catch (error) {
    noteRedirectFailure(error);
  }

  // Keeps the active account correct after an interactive login that did not come through
  // the redirect promise.
  msal.addEventCallback((event) => {
    if (
      event.eventType !== EventType.LOGIN_SUCCESS &&
      event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS
    ) {
      return;
    }
    const account = (event.payload as AuthenticationResult | null)?.account;
    if (account) msal.setActiveAccount(account);
  });

  attachMsal(msal);

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <MsalProvider instance={msal}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <TimezoneProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </TimezoneProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>,
  );
}

void bootstrap().catch((error) => {
  // Configuration errors throw at module load (see auth/config.ts). Without this the page is
  // blank with only a console message, which is the worst possible first-run experience.
  const container = document.getElementById('root');
  if (container) {
    container.innerHTML = `
      <div style="font-family:ui-sans-serif,system-ui,sans-serif;max-width:40rem;margin:4rem auto;padding:1.5rem;border:1px solid #5a2224;border-radius:.75rem;background:#3a171a;color:#f87171">
        <h1 style="margin:0 0 .5rem;font-size:1.125rem">The dashboard could not start</h1>
        <p style="margin:0;white-space:pre-wrap">${String(error instanceof Error ? error.message : error).replace(/[<>&]/g, '')}</p>
      </div>`;
  }
  console.error(error);
});
